// One step's settings — SHARED by the two paths (owner, 2026-10-15, sketch). The message
// is a saved reply from the Cerveau (never a copy); reminders and « skip if already known »
// are kept with the step. Order and ON/OFF stay per path (StepList).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { whatsappApi } from '@/lib/api';
import type { StepDef, StepSettings } from '@/lib/processApi';
import { StepBrain } from './StepBrain';

const SWITCH_FR: Record<string, string> = {
  ladder_ai_replies_enabled: 'Réponses de l’IA (Réglages)',
  ladder_photos_enabled: 'Relances photos (Réglages)',
  ladder_cover_enabled: 'Relances couverture (Réglages)',
  ladder_preview_enabled: 'Relances aperçu (Réglages)',
  ladder_payment_enabled: 'Relances paiement (Réglages)',
  ladder_milestones_enabled: 'Messages d’étape (Réglages)',
  ladder_review_enabled: 'Demande d’avis (Réglages)',
  assistant_create_orders_enabled: 'Créer les commandes (Réglages)',
  page_offer_enabled: 'Offre de pages (Réglages)',
  preview_approval_required: 'Vérification avant envoi (Réglages)',
  extras_offer_enabled: 'Offre extras (Réglages)',
  delivery_agent_first: 'Livraison — l’agent d’abord (Réglages)',
};

export function StepPanel({ def, index, on, settings, catalog, knownFields, onChange, onToggle, onBack }: {
  def: StepDef;
  index: number;
  on: boolean;
  settings: StepSettings;
  catalog: StepDef[];
  knownFields: Record<string, string>;
  onChange: (next: StepSettings) => void;
  onToggle: () => void;
  /** Phone: back to the list. */
  onBack?: () => void;
}) {
  const { t } = useT();
  const [adding, setAdding] = useState('');
  const { data: kb } = useQuery({ queryKey: ['assistant-knowledge'], queryFn: () => whatsappApi.knowledge(), staleTime: 60_000 });
  const replies = (kb?.knowledge ?? []).filter((k) => (k as { kind?: string }).kind === 'template').map((k) => String((k as { title?: string }).title ?? '')).filter(Boolean);
  const before = catalog.filter((s) => s.after.includes(def.key));
  const hours = settings.reminderHours;
  const addHour = () => {
    const h = Math.round(Number(adding));
    if (!Number.isFinite(h) || h < 0 || h > 720) return;
    onChange({ ...settings, reminderHours: [...new Set([...hours, h])].sort((a, b) => a - b).slice(0, 5) });
    setAdding('');
  };
  return (
    <section aria-label={t(def.name)} className="min-w-0 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
      <header className="flex items-start gap-3">
        {onBack && (
          <button type="button" onClick={onBack} aria-label={t('Retour aux étapes')} className="w-11 h-11 -ml-2 rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer">←</button>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-gray-500">{t('Étape {n}', { n: index + 1 })}</div>
          <h2 className="text-[22px] font-semibold text-gray-900 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>{t(def.name)}</h2>
          <p className="text-[12.5px] text-gray-600 mt-1">{t('Message et réglages partagés par les deux onglets. L’ordre et ON/OFF sont propres à chaque onglet.')}</p>
        </div>
        {def.locked ? (
          <span className="shrink-0 text-[11.5px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900">🔒 {t('verrouillée')}</span>
        ) : (
          <button type="button" role="switch" aria-checked={on} onClick={onToggle}
            className={`shrink-0 min-w-[52px] h-8 rounded-full text-[12px] font-bold cursor-pointer ${on ? 'bg-adm-gold text-[#2B2116] dark:text-[#2B2116]' : 'bg-gray-200 text-gray-600'}`}>{on ? 'ON' : 'OFF'}</button>
        )}
      </header>

      <StepBrain key={def.key} stepKey={def.key} linkedIds={settings.brainIds ?? []} onLink={(brainIds) => onChange({ ...settings, brainIds })} />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0 flex flex-col gap-1.5">
          <label htmlFor="proc-msg" className="text-[13px] font-bold text-gray-900">{t('Message')}</label>
          <select id="proc-msg" value={settings.messageRef ?? ''} onChange={(e) => onChange({ ...settings, messageRef: e.target.value || null })}
            className="w-full min-w-0 h-11 rounded-lg border border-gray-300 bg-white px-3 text-[14px] text-gray-900">
            <option value="">{t('Message intégré (aujourd’hui dans le code)')}</option>
            {settings.messageRef && !replies.includes(settings.messageRef) && <option value={settings.messageRef}>{settings.messageRef}</option>}
            {replies.map((r) => <option key={r} value={r}>{t('Réponse enregistrée « {x} »', { x: r })}</option>)}
          </select>
          <p className="text-[11.5px] text-gray-500">{t('Réutilise une réponse enregistrée du Cerveau — pas de copie.')}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-bold text-gray-900">{t('Relances si pas de réponse')}</span>
          <div className="flex flex-wrap gap-1.5">
            {hours.length === 0 && <span className="text-[12.5px] text-gray-500">{t('Aucune')}</span>}
            {hours.map((h) => (
              <span key={h} className="inline-flex items-center gap-1 h-9 pl-3 pr-1 rounded-lg bg-amber-50 text-amber-900 text-[13px] font-semibold">
                {h === 0 ? t('tout de suite') : `+ ${h} h`}
                <button type="button" aria-label={t('Retirer la relance {h} h', { h })} onClick={() => onChange({ ...settings, reminderHours: hours.filter((x) => x !== h) })}
                  className="w-7 h-7 rounded-md hover:bg-amber-100 cursor-pointer">×</button>
              </span>
            ))}
            {hours.length < 5 && (
              <span className="inline-flex items-center gap-1">
                <label htmlFor="proc-add-h" className="sr-only">{t('Heures après le début de l’étape')}</label>
                <input id="proc-add-h" inputMode="numeric" value={adding} onChange={(e) => setAdding(e.target.value.replace(/\D/g, ''))} placeholder="h"
                  className="w-16 h-9 rounded-lg border border-gray-300 px-2 text-[13px]" />
                <button type="button" onClick={addHour} className="h-9 px-3 rounded-lg border border-dashed border-adm-gold text-[13px] font-semibold text-amber-900 cursor-pointer">+ {t('relance')}</button>
              </span>
            )}
          </div>
          <p className="text-[11.5px] text-gray-500">{t('Jamais la nuit (22 h – 8 h).')}</p>
        </div>
      </div>

      <fieldset className="border border-gray-200 rounded-xl px-4 py-3">
        <legend className="px-1 text-[13px] font-bold text-gray-900">{t('Ignorer l’étape si déjà connu')}</legend>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
          {Object.entries(knownFields).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 min-h-[36px] text-[13px] text-gray-800">
              <input type="checkbox" checked={settings.skipIf.includes(k)}
                onChange={(e) => onChange({ ...settings, skipIf: e.target.checked ? [...settings.skipIf, k] : settings.skipIf.filter((x) => x !== k) })} />
              {t(label)}
            </label>
          ))}
        </div>
        <p className="text-[11.5px] text-gray-500 mt-2">{t('Marche aussi sur WhatsApp : une information donnée tôt saute l’étape.')}</p>
      </fieldset>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-gray-200 rounded-xl px-4 py-3">
          <div className="text-[13px] font-bold text-gray-900">{t('Doit venir après')}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {def.after.length === 0 && <span className="text-[12.5px] text-gray-500">{t('Rien — c’est le début')}</span>}
            {def.after.map((k) => <span key={k} className="px-2.5 py-1 rounded-full bg-gray-100 text-[12px] font-semibold text-gray-800">{t(catalog.find((s) => s.key === k)?.name ?? k)}</span>)}
          </div>
          <div className="text-[13px] font-bold text-gray-900 mt-3">{t('Doit venir avant')}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {before.length === 0 && <span className="text-[12.5px] text-gray-500">—</span>}
            {before.map((s) => <span key={s.key} className="px-2.5 py-1 rounded-full bg-gray-100 text-[12px] font-semibold text-gray-800">{t(s.name)}</span>)}
          </div>
          <p className="text-[11.5px] text-gray-500 mt-2">{t('Pourquoi : {x}.', { x: t(def.why) })}</p>
        </div>
        <div className="border border-gray-200 rounded-xl px-4 py-3">
          <div className="text-[13px] font-bold text-gray-900">{t('Branché sur')}</div>
          <p className="text-[13px] text-gray-800 mt-2">{def.todaySwitch ? t(SWITCH_FR[def.todaySwitch] ?? def.todaySwitch) : t('Aucun interrupteur aujourd’hui')}</p>
          <p className="text-[11.5px] text-gray-500 mt-2">{t('Quand le moteur lira ce processus, l’ON/OFF ici et l’interrupteur des Réglages seront le même.')}</p>
        </div>
      </div>
    </section>
  );
}
